import React, { useState } from 'react';
import { Shield, Copy, Check, AlertTriangle, XCircle, HelpCircle, FileText } from 'lucide-react';
import { Button } from './ui/button';

const InsuranceGuidanceSection = () => {
  const [copiedSection, setCopiedSection] = useState(null);

  const copyToClipboard = (text, section) => {
    navigator.clipboard.writeText(text);
    setCopiedSection(section);
    setTimeout(() => setCopiedSection(null), 2000);
  };

  const mainWording = `I am arranging fully comprehensive motor insurance for a vehicle that I will be using for my own window-cleaning business under a short-term vehicle lease agreement.

I am the main driver and policyholder. The vehicle is owned by a third party and is being made available to me for business use under a short-term lease. It is not a public self-drive hire vehicle.

Please note the vehicle owner as the registered owner on the policy. The insurance must cover commercial window-cleaning use for the full period of the agreement.`;

  const clarificationWording = `The vehicle is not hired to the public. It is made available to me as a trade user under a written lease agreement, and I am responsible for insuring it while in my possession.`;

  const policyRequirements = [
    "Be fully comprehensive",
    "Cover the specific vehicle registration number",
    "Name the user as policyholder",
    "Note the vehicle owner as owner",
    "Include commercial window-cleaning use",
    "Be valid for the entire lease period"
  ];

  const prohibitedTerms = [
    "Self-drive hire",
    "Van rental",
    "Daily hire",
    "Hire & reward",
    "Public hire"
  ];

  return (
    <section id="insurance-guidance" className="py-20 lg:py-28 bg-slate-50">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block bg-sky-100 text-sky-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Important Information
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Insurance Guidance
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Use the exact wording below when arranging your insurance policy. This ensures your cover is valid and correctly describes the arrangement.
          </p>
        </div>

        {/* Main Wording Block */}
        <div className="bg-white rounded-2xl border-2 border-sky-200 p-6 lg:p-8 mb-8 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <FileText className="w-6 h-6 text-sky-600" />
              What To Tell Your Insurer
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(mainWording, 'main')}
              className="flex items-center gap-2"
            >
              {copiedSection === 'main' ? (
                <>
                  <Check className="w-4 h-4 text-green-600" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copy Text
                </>
              )}
            </Button>
          </div>
          <div className="bg-slate-50 rounded-xl p-5 border border-slate-200">
            <p className="text-slate-700 leading-relaxed whitespace-pre-line italic">
              "{mainWording}"
            </p>
          </div>
        </div>

        {/* Policy Requirements */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 lg:p-8 mb-8 shadow-sm">
          <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2 mb-6">
            <Shield className="w-6 h-6 text-sky-600" />
            Required Policy Details (Non-Negotiable)
          </h3>
          <p className="text-slate-600 mb-4">Your policy <span className="font-semibold">must</span>:</p>
          <ul className="grid md:grid-cols-2 gap-3">
            {policyRequirements.map((req, index) => (
              <li key={index} className="flex items-start gap-3 bg-emerald-50 rounded-lg p-3">
                <Check className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <span className="text-emerald-800">{req}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-amber-800 font-semibold flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              No proof of valid insurance = No vehicle access
            </p>
          </div>
        </div>

        {/* Two Column: Prohibited Terms + Optional Clarification */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* What NOT To Say */}
          <div className="bg-white rounded-2xl border border-red-200 p-6 shadow-sm">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2 mb-4">
              <XCircle className="w-5 h-5 text-red-500" />
              What NOT To Say
            </h3>
            <p className="text-slate-600 text-sm mb-4">
              Do not describe the arrangement as any of the following – this may invalidate your cover:
            </p>
            <ul className="space-y-2">
              {prohibitedTerms.map((term, index) => (
                <li key={index} className="flex items-center gap-2 text-red-700 bg-red-50 rounded-lg px-3 py-2">
                  <XCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{term}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Optional Clarification */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-sky-600" />
                If Asked for Clarification
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(clarificationWording, 'clarification')}
                className="flex items-center gap-1 text-xs"
              >
                {copiedSection === 'clarification' ? (
                  <Check className="w-3 h-3 text-green-600" />
                ) : (
                  <Copy className="w-3 h-3" />
                )}
              </Button>
            </div>
            <p className="text-slate-600 text-sm mb-3">If your insurer asks for more details, you may add:</p>
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <p className="text-slate-700 text-sm italic">
                "{clarificationWording}"
              </p>
            </div>
          </div>
        </div>

        {/* Important Notice */}
        <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-6 lg:p-8 text-white">
          <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6" />
            Important Notice
          </h3>
          <p className="text-red-100 leading-relaxed">
            If insurance is cancelled, amended, or becomes invalid at any time, the vehicle must not be used and must be returned immediately. 
            We will physically verify your insurance certificate and schedule before releasing the vehicle.
          </p>
        </div>
      </div>
    </section>
  );
};

export default InsuranceGuidanceSection;
